import React, { useState, useEffect } from 'react';
import { CircularProgress } from '@mui/material';
import adminService from '../../services/adminService';
import '../../styles/components/admin/DoctorPatientsTab.css';

// Import icons
import SearchIcon from '../../assets/icons/search_icon.svg';

const DoctorPatientsTab = ({ doctorId }) => {
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [itemsPerPage] = useState(10);
  const [removingPatient, setRemovingPatient] = useState(null);
  const [showRemoveModal, setShowRemoveModal] = useState(false);
  const [patientToRemove, setPatientToRemove] = useState(null);
  
  useEffect(() => {
    const fetchPatients = async () => {
      if (!doctorId) {
        console.error('No doctorId provided to DoctorPatientsTab');
        setError('Doctor ID is missing');
        setLoading(false);
        return;
      }
      
      setLoading(true);
      setError(null);
      
      try {
        console.log(`Fetching patients for doctor: ${doctorId}`);
        
        // Call the API to get patients for this doctor
        const response = await adminService.getDoctorPatients(doctorId, {
          page: currentPage,
          limit: itemsPerPage,
          search: searchTerm || undefined
        });
        
        console.log('Doctor patients response:', response);
        
        // Check if response.data exists and is an array
        if (response && response.data && Array.isArray(response.data)) {
          setPatients(response.data);
          
          if (response.pagination) {
            setTotalPages(response.pagination.pages || 1);
          }
        } else if (response && response.success === true) {
          // Handle empty array response
          setPatients([]);
          setTotalPages(1);
        } else {
          throw new Error('Invalid response format');
        }
      } catch (err) {
        console.error('Error fetching patients:', err);
        
        if (err.response && err.response.status === 404) {
          setError('No patients found for this doctor.');
        } else {
          setError(`Failed to load patients: ${err.message}`);
        }
        
        setPatients([]);
      } finally {
        setLoading(false);
      }
    };
    
    fetchPatients();
  }, [doctorId, currentPage, itemsPerPage, searchTerm]);
  
  // Handle search
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1); // Reset to first page on new search
  };
  
  // Handle pagination
  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  // Handle remove patient connection
  const handleRemovePatient = (patient) => {
    setPatientToRemove(patient);
    setShowRemoveModal(true);
  };

  const confirmRemovePatient = async () => {
    if (!patientToRemove) return;

    setRemovingPatient(patientToRemove._id);
    
    try {
      // Call API to remove patient connection with doctor
      await adminService.removeDoctorPatientConnection(doctorId, patientToRemove._id);
      
      // Remove patient from local state
      setPatients(prevPatients => 
        prevPatients.filter(patient => patient._id !== patientToRemove._id)
      );
      
      // Show success message (you can replace this with a toast notification)
      console.log(`Successfully removed connection between doctor ${doctorId} and patient ${patientToRemove._id}`);
      
    } catch (err) {
      console.error('Error removing patient connection:', err);
      setError(`Failed to remove patient connection: ${err.message}`);
    } finally {
      setRemovingPatient(null);
      setShowRemoveModal(false);
      setPatientToRemove(null);
    }
  };

  const cancelRemovePatient = () => {
    setShowRemoveModal(false);
    setPatientToRemove(null);
  };
  
  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };
  
  if (loading) {
    return (
      <div className="dpt-patients-loading">
        <CircularProgress />
        <p>Loading patients...</p>
      </div>
    );
  }
  
  return (
    <div className="dpt-doctor-patients-tab">
      {error ? (
        <div className="dpt-no-data-message">{error}</div>
      ) : patients.length === 0 ? (
        <div className="dpt-no-data-message">No patients associated with this doctor.</div>
      ) : (
        <>
          <div className="dpt-patients-table">
            <div className="dpt-table-header">
              <div className="dpt-th">Patient Name</div>
              <div className="dpt-th">Email</div>
              <div className="dpt-th">Status</div>
              <div className="dpt-th">Actions</div>
            </div>
            
            <div className="dpt-table-body">
              {patients.map((patient, index) => (
                <div key={patient._id || index} className={`dpt-table-row ${index % 2 === 0 ? 'dpt-even' : 'dpt-odd'}`}>
                  <div className="dpt-td dpt-patient-name">
                    <div className="dpt-patient-avatar">
                      {patient.profilePicture ? (
                        <img src={patient.profilePicture} alt={`${patient.firstName} ${patient.lastName}`} />
                      ) : (
                        <div className="dpt-patient-initials">
                          {patient.firstName?.[0] || '?'}{patient.lastName?.[0] || '?'}
                        </div>
                      )}
                    </div>
                    <span>{patient.firstName || 'Unknown'} {patient.lastName || ''}</span>
                  </div>
                  <div className="dpt-td">{patient.email || 'N/A'}</div>
                  <div className="dpt-td">
                    {patient.lastAppointmentStatus ? (
                      <span className={`dpt-status-badge dpt-${patient.lastAppointmentStatus.toLowerCase()}`}>
                        {patient.lastAppointmentStatus.toUpperCase()}
                      </span>
                    ) : (
                      'N/A'
                    )}
                  </div>
                  <div className="dpt-td dpt-actions-cell">
                    <button 
                      className="dpt-remove-patient-btn"
                      onClick={() => handleRemovePatient(patient)}
                      disabled={removingPatient === patient._id}
                      title="Remove patient connection"
                    >
                      {removingPatient === patient._id ? (
                        <CircularProgress size={16} color="inherit" />
                      ) : (
                        'Remove'
                      )}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
          
          {/* Pagination */}
          {totalPages > 1 && (
            <div className="dpt-pagination">
              <button 
                className="dpt-page-nav dpt-prev"
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
              >
                &lt;
              </button>
              
              <div className="dpt-page-numbers">
                {[...Array(totalPages)].map((_, index) => (
                  <button
                    key={index}
                    className={`dpt-page-number ${currentPage === index + 1 ? 'dpt-active' : ''}`}
                    onClick={() => handlePageChange(index + 1)}
                  >
                    {index + 1}
                  </button>
                ))}
              </div>
              
              <button 
                className="dpt-page-nav dpt-next"
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
              >
                &gt;
              </button>
            </div>
          )}
        </>
      )}

      {/* Remove Patient Confirmation Modal */}
      {showRemoveModal && patientToRemove && (
        <div className="dpt-remove-modal-backdrop">
          <div className="dpt-remove-modal">
            <div className="dpt-remove-modal-header">
              <h3>Remove Patient Connection</h3>
            </div>
            <div className="dpt-remove-modal-body">
              <p>
                Are you sure you want to remove the connection between this doctor and{' '}
                <strong>{patientToRemove.firstName} {patientToRemove.lastName}</strong>?
              </p>
              <p className="dpt-remove-modal-warning">
                This action will disconnect the patient from this doctor's care. The patient's data will remain intact, 
                but they will no longer be associated with this doctor.
              </p>
            </div>
            <div className="dpt-remove-modal-footer">
              <button 
                className="dpt-remove-modal-btn dpt-cancel-btn" 
                onClick={cancelRemovePatient}
                disabled={removingPatient}
              >
                Cancel
              </button>
              <button 
                className="dpt-remove-modal-btn dpt-confirm-btn" 
                onClick={confirmRemovePatient}
                disabled={removingPatient}
              >
                {removingPatient ? 'Removing...' : 'Remove Connection'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DoctorPatientsTab;